<template>
  <div>
    <div v-if="!isYouTubeLink" :class="smaller ? 'p-2 bg-white' : 'p-3 bg-white larger'">
      <div v-if="isVideoPending">No preview available.</div>
      <div v-else>
        <video v-if="path.toLowerCase().endsWith('mp4')"
               controls
               height="auto"
               width="200">
          <source :src="path" type="video/mp4">
          This Video format is not supported.
        </video>
      </div>
    </div>
    <div v-else-if="videoUrl" :class="smaller ? 'p-2 bg-white' : 'p-3 bg-white larger'">
      <div class="d-flex justify-content-center">
        <div v-if="!smaller" class="iframe-container">
          <iframe :src="videoUrl"
                  allowfullscreen=""
                  class="videoIframe"
                  width="358"
                  height="200"></iframe>
        </div>
        <div v-else-if="thumbnail" class="pb-2">
          <img :src="thumbnail"
               class="img-fluid"
               loading="lazy"
               alt="thumbnail of youtube video" />
        </div>
      </div>

      <div class="info-container d-flex">
        <div class="square text-center text-white h4 mb-0">{{ctaLetter}}</div>
        <div class="flex-grow-1 d-flex flex-column px-2">
          <div class="name font-weight-bold">{{ctaTitle}}</div>
          <div class="d-flex">
            <div class="ad-label d-inline-block text-white">Ad</div>
            <div class="ad-text ml-2 text-muted">{{ctaUrl}}</div>
          </div>
        </div>
        <div class="button text-white px-2 ml-auto">{{ctaText}}</div>
      </div>
    </div>
    <t-alert v-else show variant="warning" class="mb-0">
      There was a problem with the YouTube video URL
    </t-alert>

    <div v-if="videoUrl && !smaller && !isVideoPending" class="mt-4 px-3">
      <a :href="path" target="_blank" class="d-flex-start-start">
        <span class="flex-grow-1">{{path}}</span>
        <fluency-icon type="linkOut" class="ml-2 mt-2" />
      </a>
    </div>
  </div>
</template>

<script>
import { getImageSourceFromYouTubeVideo } from 'core-ui/utils/youtube'

export default {
  name: 'adPreviewYoutube',
  props: {
    creativePlan: {
      type: Object,
      default: () => { return {} }
    },
    smaller: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ctaLetter () {
      // Don't know what this is supposed to be. Ojala said: "just ignore that bit, put in an F or something"

      return 'F'
    },
    ctaText () {
      return this.ctaItem('callToActionText', 'CTA')
    },
    ctaTitle () {
      return this.ctaItem('title', 'Title')
    },
    ctaUrl () {
      return this.ctaItem('display_url', 'www.example.com')
    },
    path () {
      const videoAssets = this.creativePlan?.adSpec?.videoAssets
      if (videoAssets?.length > 0) {
        for (const asset of videoAssets) {
          if (asset.locationInAd === 'videoUrl') {
            return asset.path
          }
        }
      }
      return ''
    },
    videoUrl () {
      const url = this.youTubeUrlFormatter(this.path)
      return `${url}?modestbranding=1`
    },
    isYouTubeLink () {
      return ['youtu.be/', 'youtube.com/'].some(pattern => {
        return this.path.indexOf(pattern) >= 0
      })
    },
    isVideoPending () {
      return ['VIDEO_URL_PENDING'].includes(this?.path)
    },
    thumbnail () {
      if (!this.path) {
        return ''
      }

      return getImageSourceFromYouTubeVideo(this.path)
    }
  },
  methods: {
    youTubeUrlFormatter (url) {
      const protocol = url.startsWith('http://') ? 'http://' : 'https://'
      const domain = 'www.youtube.com'
      let id = url.trim()
      const lastChar = id.substring(id.length - 1, id.length)

      // 1) REMOVE TRAILING `/`
      if (lastChar === '/') {
        id = id.substring(0, id.length - 1)
      }

      // 2) REMOVE URL & QUERY STRING SYNTAX
      id = id.replace('/watch?v=', '/')

      // 3) REMOVE EVERYTHING BEFORE FINAL `/`
      id = id.substring(id.lastIndexOf('/') + 1)

      return `${protocol}${domain}/embed/${id}`
    },
    ctaItem (type = 'callToActionText', defaultVal = '') {
      const textAssets = this.creativePlan?.adSpec?.textAssets
      let str = defaultVal

      if (textAssets) {
        if (textAssets.length) {
          textAssets.forEach(n => {
            if (n.locationInAd === type) {
              str = n.text
            }
          })
        }
      }

      return str
    }
  }
}
</script>

<style lang="scss" scoped>
.phone {
  background-image: url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAScAAAE7CAQAAAA1R48qAAAGsElEQVR4AezbQU4iQRQG4NqMF5EbuPYYsOMSHgFn5RHYcgEOQFc9yg5xAQmho2FHIEJoekVXAZCC/kdnREDQUVoXM77vv0IlVfnfK3EMlaG8ulRlatNQxdJ54Pz7kU7FNKS2KqtLyquM+Gq3J9WcKtFI21bcWYUwmMEhAfsfJHCYwSBEZ9WKtaWRKlVztyfiK1yfyaIy9XEfU7DvYIo+6mNlZPH6THwmOlcVbbvLOdh3M0d3qa2q0Ln4DCqjKr4ZJAnYd5VgkPhGVVK+p+o/ZIEmPZeA8ZHqOZrIQv2HOI53SkHTLsDYHws0LQXeqfg4ylJ8vwRjO+6XFFNWfIy80NaAsX0G2soL8X7yqmbnYOywOWpWXr37MN0YB8Ze53Bj3nWg5EXNOrANhwjdh0RwYGsOtb9feZTVfM3tCKHRwN1DGtAIwdbm0Jayb1cDsQHbCOAjgnlKBA8B2JoBxd7pq6UlBVwNbAvhYQyzlTF8hNjCtUHwSrEpC00L9sxBI4J5kQgaDmytaWXh8A7TZAG2EaEBcyANRGBrC9DkwCxPVXoOW1gPdzAHcocu2EbPqcreCopvEnwcHyeWwDcv1ldUZZCA8WV3lEGiKjubltomYPwUP04Cbbc2NmWxuwTbE8LfKwo8hGAvdZey+PyhQJk52CEBvJ0a00cAtm8OZZ4+KVRz9TFS4SELq4+rOfFIlfp4G4+Ae+i9OQJmfaiSeESjKdJhbAoa/W7DtUVqjGmrMoLyrRipMdaKKS+8n50VUmOss1KXQpVDpMdYCFUW1DZIjzEDagsazpAeYzPQUKjYIT3GHFQs5K927gFLkjSAwuhsuo57C2Mjqm3btm3bdnfma2ejMIg/p+Jk3fu28CWCL9qhPtpZ/eKrVSkDVkVOyAk5IScollM7J7Mxq8yyMSfTrpfTyezN00DyNHtzsl5OG8XEJ0FtrJfTqsDAHuSEnJATcpITckJOyAnkhJyQE3KSE3JCTsgJ5ISckBO9Q07ICTmBnJATckJOckJOyAk5gZyQE3KiZ8gJOSEnkBNyQk7ISU7ICTkhJ5ATckJO9Aw5ISfkBHJCTsgJOckJOSEn5ARyQk7IiZ4hJ+SEnEBOyAk5ISc5ISfkhJxATsgJOdEz5ISckBPICTkhJ+QkJ+SEnJATyAk5ISd6hpyQE3ICOSEn5ISc5ISckBNyAjkhJ+REz5ATckJOICfkhJyQk5yQE3JCTiAn5ISc6BlyQk7ICeSEnJATcpITckJOyAnkhJyQEz1DTsgJOYGckBNyQk5yQk7ICTmBnJATcmJEyQk5gZyQE3JCTnJCTsgJOZ3tzKzgtxPICTkhJ+QkJ+SEnJATyAk5ISd6hZyQE3ICOSEnRsCjfJNf8lJOlHA0fa93VU6U0MryrE/khP9ONMzj/Jg/05ITJRxJ3+tdkRMlvMzCrEzkhP9ONMyT/J7xacsJ/51omJeZkyWJnPDfCTkhJzkhJ+SEnOSEnJATcgI5ISfkRE+QE15Tb15TD3JCTsgJOckJOSEn5ARyQk7IiR4gJ+SEnEBOyAk50QByQk43ujC7PVpzWtWF2WY5mZzkJCc5yUlOJic5yUlOcpKTycnkJCc5yUlO/5icTE5HujA7OTpyAjkhp5HA6Yz9ZFPyRE51sDx97zcm5xI51cOyTkyj9kTB7pQkqDE5m8ipDO4kcuom5ISc5CQnOclJTshJTnKSk5zkhJyQk5zktL4h2x/c74ScQE7IaSQgJ+SEnEBOyAk5tXIsRSCnVlakyvHUg5w6MVXZlnqQUyemKqdSztMszvjPNjnHkhzN+NdblHKehsbk1MrKVO93L+VsTjVgk5McTvV681POmWwJDclpVar3m5RynmfCIDmN71JOlaBGMqeXufV2ycVUg252Huad+zk77G5kMIdS/auczufssHuRoR1N9XpbM0LkdCvV27VyKtUQ21srp5n/S07XsjaTU6USVNNz2lkjpwupup7T409+pgXV+Jz218hpeddzup/pqQZsWp6EBubUnwc1cprc5ZxamTVoTPdDI3NakdTIaVKXc9rTOfGwKKuz9H1M90JDc7pUK6dFqQbdhCTnsvT1ttTI6V7GpT+bcjXtzomCqWJqbk6zPzvnfGfYPcxAj7M3O95v9YDvvMHczZ1h10pHdmfuZxGfEVOzczqScg6ner/5eZYS1uVZPnVVTE3J6Xb6B8Q0NS9SzrrCMb3M49ConB5nz9u1k1zJ3uz5ZAfyICXNKhcT7sacICY5lbMv88Qkp3JeBjkhJzkhJ+SEnChETiAn5ISckNMrVHehkHdT0Y8AAAAASUVORK5CYII=");
  background-position: top center;
  background-repeat: no-repeat;
  &.larger {
    min-width: 295px;
  }
}
.iframe-container {
  width: 358px;
}
.info-container {
  .square {
    width: 30px;
    height: 30px;
    padding-top: 5px;
    background-color: #2d5e1c;
  }
  .ad-label {
    background-color: #ecc740;
    font-size: 10px;
    line-height: 12px;
    padding-left: 1px;
    padding-right: 1px;
  }
  .ad-text {
    line-height: 1em;
  }
  .button {
    font-size: 12px;
    line-height: 26px;
    height: 26px;
    font-weight: 300;
    background-color: #2388e2;
    text-transform: uppercase;
  }
}
</style>
